import { Injectable, NgZone, inject } from '@angular/core';

/**
 * Turns every data table into a stacked card list on phones without touching the templates.
 *
 * A table qualifies when it sits in a horizontal-scroll wrapper (`.overflow-x-auto`) and has a
 * `<thead>`; add `data-no-cards` on a table to opt out. Each body cell gets `data-label` = its
 * column header text, and the table gets the `rt-cards` class; the actual card layout lives in
 * styles.scss under `@media (max-width: 639px)`, so tablets and desktops keep the plain table.
 */
@Injectable({ providedIn: 'root' })
export class ResponsiveTableService {
  private zone = inject(NgZone);
  private scheduled = false;

  start(): void {
    if (typeof window === 'undefined' || typeof MutationObserver === 'undefined') return;
    this.zone.runOutsideAngular(() => {
      const observer = new MutationObserver(() => this.schedule());
      observer.observe(document.body, { childList: true, subtree: true });
      this.schedule();
    });
  }

  private schedule(): void {
    if (this.scheduled) return;
    this.scheduled = true;
    requestAnimationFrame(() => {
      this.scheduled = false;
      this.labelTables();
    });
  }

  private labelTables(): void {
    document.querySelectorAll<HTMLTableElement>('.overflow-x-auto > table:not([data-no-cards])').forEach((table) => {
      const headRow = table.tHead?.rows[0];
      if (!headRow) return;
      // Header labels by column index, expanding colspans.
      const labels: string[] = [];
      const headIdx: number[] = []; // column index -> which <th> it belongs to
      Array.from(headRow.cells).forEach((th, idx) => {
        const text = (th.textContent || '').replace(/\s+/g, ' ').trim();
        for (let i = 0; i < (th.colSpan || 1); i++) {
          labels.push(text);
          headIdx.push(idx);
        }
      });
      if (!table.classList.contains('rt-cards')) table.classList.add('rt-cards');
      for (const body of Array.from(table.tBodies)) {
        for (const row of Array.from(body.rows)) {
          let col = 0;
          const groups = new Map<number, HTMLTableCellElement[]>();
          for (const cell of Array.from(row.cells)) {
            const label = cell.colSpan > 1 ? '' : labels[col] ?? '';
            if (cell.getAttribute('data-label') !== label) cell.setAttribute('data-label', label);
            if (cell.colSpan <= 1 && label !== '') {
              const h = headIdx[col];
              groups.set(h, [...(groups.get(h) ?? []), cell]);
            }
            col += cell.colSpan || 1;
          }
          // One header spanning several cells (e.g. "Club" = logo + code): the logo/initial cell is hidden on
          // phones so the card shows a single captioned value instead of a duplicated, confusing pair.
          for (const cells of groups.values()) {
            const isAvatar = (c: HTMLTableCellElement) => (c.textContent || '').replace(/\s+/g, '').length <= 2;
            const hasText = cells.some((c) => !isAvatar(c));
            for (const c of cells) {
              const hide = cells.length > 1 && hasText && isAvatar(c);
              if (hide !== c.hasAttribute('data-avatar')) hide ? c.setAttribute('data-avatar', '') : c.removeAttribute('data-avatar');
            }
          }
        }
      }
    });
  }
}
